import { ActionTree } from "vuex";
import { RootState } from "..";
import { State } from "./state";
import { MutationTypes } from "./mutations";
import deviceService from "@/services/device.service";
import settingsService from "@/services/settings.service";
import * as signalR from "@microsoft/signalr";
import AuthService from "@/services/auth.service";

export enum ActionTypes {
  FetchDevices = "FETCH_DEVICES",
  FetchSettings = "FETCH_SETTINGS",
  ConnectLiveServer = "CONNECT_LIVE_SERVER",
  StartRecovery = "START_RECOVERY",
  StopRecovery = "STOP_RECOVERY",
  StartIdentification = "START_IDENTIFICATION",
  StopIdentification = "STOP_IDENTIFICATION",
  RequestLocation = "REQUEST_LOCATION",
  ChangeLifetime = "CHANGE_LIFETIME",
  InviteActivatorApp = "INVITE_ACTIVATOR_APP",
  UpdateDeviceLabel = "UPDATE_DEVICE_LABEL",
}

export const actions: ActionTree<State, RootState> = {
  async [ActionTypes.FetchDevices]({ commit }) {
    const devices = await deviceService.getActive();
    commit(MutationTypes.SetDevices, devices);
  },
  async [ActionTypes.ConnectLiveServer]({ commit }) {
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl("/livedata", {
        async accessTokenFactory() {
          return await AuthService.getToken() ?? "";
        }
      })
      .configureLogging(signalR.LogLevel.Error)
      .withAutomaticReconnect()
      .build();

    // Starts the SignalR connection
    await hubConnection.start();

    commit(MutationTypes.SetLiveEventsConnected, true);

    hubConnection.on("message", (message) => commit(MutationTypes.AddLiveMessage, message));
  },
  async [ActionTypes.FetchSettings]({ commit }) {
    const settings = await settingsService.getActive();
    commit(MutationTypes.SetSettings, settings);
  },
  async [ActionTypes.StartRecovery]({ commit }, serialNumber: string) {
    const output = await deviceService.startRecovery(serialNumber);
    if (output) {
      const updatedDevice = await deviceService.get(serialNumber);
      commit(MutationTypes.SetRecoveryStarted, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.StopRecovery]({ commit }, serialNumber: string) {
    const output = await deviceService.stopRecovery(serialNumber);
    if (output) {
      const updatedDevice = await deviceService.get(serialNumber);
      commit(MutationTypes.SetRecoveryStopped, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.StartIdentification]({ commit }, serialNumber: string) {
    const output = await deviceService.startIdentification(serialNumber);
    if (output) {
      const updatedDevice = await deviceService.get(serialNumber);
      commit(MutationTypes.SetIdentificationStarted, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.StopIdentification]({ commit }, serialNumber: string) {
    const output = await deviceService.stopIdentification(serialNumber);
    if (output) {
      const updatedDevice = await deviceService.get(serialNumber);
      commit(MutationTypes.SetIdentificationStopped, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.RequestLocation]({ commit }, serialNumber: string) {
    const output = await deviceService.requestLocation(serialNumber);
    if (output) {
      const updatedDevice = await deviceService.get(serialNumber);
      commit(MutationTypes.SetLocationRequested, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.ChangeLifetime]({ commit }, { device, lifetime }) {
    const output = await deviceService.setLifetime(device, lifetime);
    if (output) {
      const updatedDevice = await deviceService.get(device);
      commit(MutationTypes.SetLifetimeRequested, updatedDevice);
      return true;
    }
  },
  async [ActionTypes.InviteActivatorApp]({ commit }, email: string) {
    return await deviceService.inviteActivatorApp(email);
  },
  async [ActionTypes.UpdateDeviceLabel]({ commit }, { serialNumber, label }) {
    await deviceService.ChangeDeviceLabel(serialNumber, label);
    commit(MutationTypes.UpdateDeviceLabel, { serialNumber, label });
    return true;
  },
};
